// QuotPrintPDF.jsx - Quote print component using @react-pdf/renderer
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  PDFDownloadLink,
  Image,
  Font,
} from "@react-pdf/renderer";
import formatPrice from "../../utils/formatPrice";

// Register fonts if needed (optional - for better typography)
// Font.register({
//   family: 'Roboto',
//   src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf'
// });

// Create styles - No Tailwind, using react-pdf style objects
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    padding: 20,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  // Header styles
  headerContainer: {
    border: "1pt solid black",
    marginBottom: 0,
  },
  companyHeader: {
    borderBottom: "2pt solid black",
    padding: 5,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  companyInfo: {
    flex: 3,
    justifyContent: "center",
  },
  companyName: {
    fontSize: 24,
    color: "#33adee",
    textAlign: "center",
    marginBottom: 10,
  },
  companyServices: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 2,
  },
  logoSection: {
    flex: 1,
    // Uncomment when logo is available
    // alignItems: 'center',
    // justifyContent: 'center',
  },
  logo: {
    width: 80,
    height: 80,
  },
  // Quote info row
  quoteInfoRow: {
    flexDirection: "row",
    borderBottom: "1pt solid black",
  },
  quoteInfoCell: {
    flex: 1,
    padding: 4,
    fontSize: 10,
    borderRight: "1pt solid black",
  },
  quoteInfoCellLast: {
    flex: 1,
    padding: 4,
    fontSize: 10,
  },
  quoteNumber: {
    color: "#ff0000",
  },
  // Customer info
  customerRow: {
    borderBottom: "1pt solid black",
    padding: 4,
    fontSize: 10,
  },
  customerRowLast: {
    borderBottom: "2pt solid black",
    padding: 4,
    fontSize: 10,
  },
  // Table header
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#33adee",
    borderBottom: "1pt solid black",
  },
  tableHeaderCell: {
    flex: 1,
    color: "white",
    textAlign: "center",
    padding: 8,
    borderRight: "1pt solid black",
  },
  tableHeaderCellLast: {
    flex: 1,
    color: "white",
    textAlign: "center",
    padding: 8,
  },
  // Table body
  tableContainer: {
    borderLeft: "1pt solid black",
    borderRight: "1pt solid black",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid black",
    minHeight: 20,
  },
  tableCell: {
    flex: 1,
    padding: 4,
    textAlign: "center",
    borderRight: "1pt solid black",
    justifyContent: "center",
  },
  tableCellLast: {
    flex: 1,
    padding: 4,
    textAlign: "center",
    justifyContent: "center",
  },
  tableBottom: {
    borderBottom: "1pt solid black",
  },
  // Footer styles
  footerContainer: {
    marginTop: 0,
  },
  footerMainRow: {
    flexDirection: "row",
    minHeight: 120,
  },
  paymentSection: {
    flex: 2,
    borderRight: "1pt solid black",
    padding: 10,
    justifyContent: "center",
  },
  thanksText: {
    color: "#ff0000",
    fontSize: 14,
    textAlign: "center",
    marginBottom: 10,
  },
  footerText: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 3,
  },
  totalsSection: {
    flex: 1,
    border: "1pt solid black",
  },
  totalsRow: {
    flexDirection: "row",
    borderBottom: "1pt solid black",
  },
  totalsRowLast: {
    flexDirection: "row",
  },
  totalsLabel: {
    flex: 1,
    padding: 6,
    textAlign: "center",
    borderRight: "1pt solid black",
  },
  totalsValue: {
    flex: 1,
    padding: 6,
    textAlign: "center",
  },
  // Brand stripe
  brandStripe: {
    height: 20,
    backgroundColor: "#33adee",
    border: "1pt solid black",
    marginTop: 0,
  },
  // Contact info
  contactContainer: {
    border: "1pt solid black",
    padding: 10,
    marginTop: 0,
  },
  contactText: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 3,
  },
  contactRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 3,
  },
  redText: {
    color: "#ff0000",
  },
  blueText: {
    color: "#33adee",
  },
  // Simple header for page 2+
  simpleHeaderContainer: {
    border: "1pt solid black",
    marginBottom: 0,
  },
  simpleQuoteRow: {
    flexDirection: "row",
    borderBottom: "1pt solid black",
  },
});

/**
 * QuotPrintPDF Component - Professional quote/invoice PDF generator
 *
 * Features:
 * - Clean A4 layout with proper page breaks
 * - Multi-page support with consistent headers
 * - No white space issues on page 2+
 * - Company branding and professional styling
 * - Footer only on last page
 * - Image/logo support
 * - Supports both Invoice and Quotation modes
 *
 * Props:
 * - quote: Object containing invoice/quote data
 * - isQuotation: Boolean to determine if rendering as quotation (true) or invoice (false)
 *
 * Usage:
 * <QuotPrintPDF quote={invoiceData} isQuotation={false} /> // For Invoice
 * <QuotPrintPDF quote={invoiceData} isQuotation={true} />  // For Quotation
 */
const QuotPrintPDF = ({ quote, isQuotation = false }) => {
  // Calculate totals
  const calculateSubTotal = () => {
    let subTotal = 0;
    if (quote?.items) {
      quote.items.forEach((item) => {
        subTotal += item.price * item.quantity;
      });
    }
    return subTotal;
  };

  const calculateVAT = () => {
    // If VAT is already calculated and passed in the quote object, use that
    if (quote?.vat !== undefined && quote?.vat !== null) {
      return quote.vat;
    }

    // Otherwise calculate it - VAT on full subtotal (discount applied after VAT)
    const subtotal = calculateSubTotal();
    const vat = subtotal * 0.2; // 20% VAT on full subtotal
    return Math.max(0, vat);
  };

  const calculateTotal = () => {
    // If total is already calculated and passed in the quote object, use that
    if (quote?.amount !== undefined && quote?.amount !== null) {
      return quote.amount;
    }

    // Otherwise calculate it: Subtotal + VAT - Discount
    const subtotal = calculateSubTotal();
    const vat = calculateVAT();
    const discount = quote?.discount > 0 ? quote.discount : 0;
    return subtotal + vat - discount;
  };

  const calculateTotalPaid = () => {
    let totalPaid = 0;
    if (quote?.payments) {
      quote.payments.forEach((payment) => {
        totalPaid += payment.amount;
      });
    }
    return totalPaid;
  };

  const calculateTotalToPay = () => {
    const total = calculateTotal();
    const totalPaid = calculateTotalPaid();
    return total - totalPaid;
  };

  // Split items into pages (18 items per page)
  const itemsPerPage = 18;
  const paginateItems = () => {
    if (!quote?.items) return [];

    const pages = [];
    const totalItems = quote.items.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    for (let i = 0; i < totalPages; i++) {
      const startIndex = i * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
      const pageItems = quote.items.slice(startIndex, endIndex);

      // Add empty rows to maintain consistent layout
      const emptyRowsCount = itemsPerPage - pageItems.length;
      const emptyRows = Array(emptyRowsCount).fill(null);

      pages.push({
        items: pageItems,
        emptyRows: emptyRows,
        pageNumber: i + 1,
        totalPages: totalPages,
        isFirstPage: i === 0,
        isLastPage: i === totalPages - 1,
      });
    }

    return pages;
  };

  const pages = paginateItems();

  // Render full header (for first page)
  const renderFullHeader = (pageNumber, totalPages) => (
    <View style={styles.headerContainer}>
      <View style={styles.companyHeader}>
        <View style={styles.companyInfo}>
          <Text style={styles.companyName}>
            {import.meta.env.VITE_PUBLIC_COMPANY_NAME}
          </Text>
          <Text style={styles.companyServices}>
            CARPET . LAMINATE . VINYL . RUGS
          </Text>
          <Text style={styles.companyServices}>
            LVT FLOORING . SAFETY FLOORING
          </Text>
          <Text style={styles.companyServices}>
            ARTIFICIAL GRASS . DOUBLE GLAZING
          </Text>
        </View>
        <View style={styles.logoSection}>
          {/* Uncomment when logo is available */}
          {/* <Image style={styles.logo} src="/logo512.png" /> */}
        </View>
      </View>

      <View style={styles.quoteInfoRow}>
        <View style={styles.quoteInfoCell}>
          <Text style={styles.quoteNumber}>
            {isQuotation ? "QUOTATION" : "INVOICE"} NO.:{" "}
            {quote?.invoice || "N/A"}
          </Text>
        </View>
        <View style={styles.quoteInfoCell}>
          <Text>DATE: {quote?.date || "N/A"}</Text>
        </View>
        <View style={styles.quoteInfoCellLast}>
          <Text style={{ textAlign: "center" }}>
            Page {pageNumber} of {totalPages}
          </Text>
        </View>
      </View>

      <View style={styles.customerRow}>
        <Text>CUSTOMER NAME: {quote?.name || "N/A"}</Text>
      </View>
      <View style={styles.customerRow}>
        <Text>ADDRESS: {quote?.address || "N/A"}</Text>
      </View>
      <View style={styles.customerRowLast}>
        <Text>PHONE NO.: {quote?.phone || "N/A"}</Text>
      </View>

      <View style={styles.tableHeader}>
        <Text style={styles.tableHeaderCell}>PRODUCT</Text>
        <Text style={styles.tableHeaderCell}>QUANTITY / SIZE</Text>
        <Text style={styles.tableHeaderCell}>PRICE / UNIT</Text>
        <Text style={styles.tableHeaderCellLast}>TOTAL</Text>
      </View>
    </View>
  );

  // Render simple header (for subsequent pages)
  const renderSimpleHeader = (pageNumber, totalPages) => (
    <View style={styles.simpleHeaderContainer}>
      <View style={styles.simpleQuoteRow}>
        <View style={styles.quoteInfoCell}>
          <Text style={styles.quoteNumber}>
            {isQuotation ? "QUOTATION" : "INVOICE"} NO.:{" "}
            {quote?.invoice || "N/A"}
          </Text>
        </View>
        <View style={styles.quoteInfoCell}>
          <Text>DATE: {quote?.date || "N/A"}</Text>
        </View>
        <View style={styles.quoteInfoCellLast}>
          <Text style={{ textAlign: "center" }}>
            Page {pageNumber} of {totalPages}
          </Text>
        </View>
      </View>

      <View style={styles.tableHeader}>
        <Text style={styles.tableHeaderCell}>PRODUCT</Text>
        <Text style={styles.tableHeaderCell}>QUANTITY / SIZE</Text>
        <Text style={styles.tableHeaderCell}>PRICE / UNIT</Text>
        <Text style={styles.tableHeaderCellLast}>TOTAL</Text>
      </View>
    </View>
  );

  // Render footer (only on last page)
  const renderFooter = () => (
    <View style={styles.footerContainer}>
      <View style={styles.footerMainRow}>
        <View style={styles.paymentSection}>
          <Text style={styles.thanksText}>
            Thanks for considering our services.
          </Text>
          <Text style={styles.footerText}>
            This quote is valid for 30 days from the date above.
          </Text>
          <Text style={styles.footerText}>
            Payment terms and{" "}
            <Text style={styles.blueText}>
              {import.meta.env.VITE_PUBLIC_COMPANY_NAME}
            </Text>{" "}
            account details:
          </Text>
          <Text style={styles.footerText}>
            Bank:{" "}
            <Text style={styles.blueText}>
              {import.meta.env.VITE_PUBLIC_COMPANY_BANK || "Barclays"}
            </Text>{" "}
            Sort code:{" "}
            <Text style={styles.blueText}>
              {import.meta.env.VITE_PUBLIC_COMPANY_SORT_CODE || "23-11-85"}
            </Text>{" "}
            Acc no.:{" "}
            <Text style={styles.blueText}>
              {import.meta.env.VITE_PUBLIC_COMPANY_ACC_NO || "33377733"}
            </Text>
          </Text>
          <Text style={styles.footerText}>
            Pay ref.: quote no.
            <Text style={styles.blueText}>{quote?.invoice || "N/A"}</Text>
          </Text>
          <Text style={styles.footerText}>
            VAT NUMBER:{" "}
            <Text style={styles.blueText}>
              {import.meta.env.VITE_PUBLIC_COMPANY_VAT || "483 689335"}
            </Text>
          </Text>
        </View>

        <View style={styles.totalsSection}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Subtotal</Text>
            <Text style={styles.totalsValue}>
              {formatPrice(calculateSubTotal())}
            </Text>
          </View>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>VAT (20%)</Text>
            <Text style={styles.totalsValue}>
              {formatPrice(calculateVAT())}
            </Text>
          </View>

          {quote?.discount > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Discount</Text>
              <Text style={styles.totalsValue}>
                -{formatPrice(quote.discount)}
              </Text>
            </View>
          )}

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total</Text>
            <Text style={styles.totalsValue}>
              {formatPrice(calculateTotal())}
            </Text>
          </View>

          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Paid</Text>
            <Text style={styles.totalsValue}>
              {formatPrice(calculateTotalPaid())}
            </Text>
          </View>

          <View style={styles.totalsRowLast}>
            <Text style={styles.totalsLabel}>Balance Due</Text>
            <Text style={styles.totalsValue}>
              {formatPrice(calculateTotalToPay())}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.brandStripe} />

      <View style={styles.contactContainer}>
        <Text style={styles.contactText}>
          {import.meta.env.VITE_PUBLIC_COMPANY_ADDRESS ||
            "212-216 Picton Road, Wavertree, Liverpool, L15 4LL"}
        </Text>
        <View style={styles.contactRow}>
          <Text style={styles.contactText}>
            <Text style={styles.redText}>Email: </Text>{" "}
            {import.meta.env.VITE_PUBLIC_COMPANY_EMAIL ||
              "info@princessfloorings.com"}
            {"     "}
            <Text style={styles.redText}>Website: </Text>{" "}
            {import.meta.env.VITE_PUBLIC_COMPANY_WEBSITE ||
              "princessfloorings.com"}
          </Text>
        </View>
        <View style={styles.contactRow}>
          <Text style={styles.contactText}>
            <Text style={styles.redText}>Tel:</Text>{" "}
            {import.meta.env.VITE_PUBLIC_COMPANY_TELEPHONE || "0151 709 4943"}
            {"    "}
            <Text style={styles.redText}>Mobile:</Text>{" "}
            {import.meta.env.VITE_PUBLIC_COMPANY_MOBILE || "0731 033 6073"}
          </Text>
        </View>
      </View>
    </View>
  );

  // Main document
  return (
    <Document
      title={`${isQuotation ? "Quotation" : "Invoice"} #${
        quote?.invoice || "N/A"
      }`}
    >
      {pages.map((page) => (
        <Page key={page.pageNumber} size="A4" style={styles.page}>
          {/* Render appropriate header */}
          {page.isFirstPage
            ? renderFullHeader(page.pageNumber, page.totalPages)
            : renderSimpleHeader(page.pageNumber, page.totalPages)}

          {/* Items table */}
          <View style={styles.tableContainer}>
            {/* Render items */}
            {page.items.map((item, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={styles.tableCell}>{item?.title || "N/A"}</Text>
                <Text style={styles.tableCell}>{item?.quantity || 0}</Text>
                <Text style={styles.tableCell}>
                  {formatPrice(Number(item?.price) || 0)}
                </Text>
                <Text style={styles.tableCellLast}>
                  {formatPrice(
                    (item?.quantity || 0) * (Number(item?.price) || 0)
                  )}
                </Text>
              </View>
            ))}

            {/* Empty rows */}
            {page.emptyRows.map((_, index) => (
              <View key={`empty-${index}`} style={styles.tableRow}>
                <Text style={styles.tableCell}> </Text>
                <Text style={styles.tableCell}> </Text>
                <Text style={styles.tableCell}> </Text>
                <Text style={styles.tableCellLast}> </Text>
              </View>
            ))}
          </View>

          <View style={styles.tableBottom} />

          {/* Footer only on last page */}
          {page.isLastPage && renderFooter()}
        </Page>
      ))}
    </Document>
  );
};

// Component to render PDF viewer in browser
export const QuotPrintViewer = ({ quote, isQuotation = false }) => (
  <PDFViewer width="100%" height="600px">
    <QuotPrintPDF quote={quote} isQuotation={isQuotation} />
  </PDFViewer>
);

// Component to render download link
export const QuotPrintDownloadLink = ({ quote, isQuotation = false }) => (
  <PDFDownloadLink
    document={<QuotPrintPDF quote={quote} isQuotation={isQuotation} />}
    fileName={`${isQuotation ? "quotation" : "invoice"}-${
      quote?.invoice || "document"
    }.pdf`}
  >
    {({ blob, url, loading, error }) =>
      loading
        ? "Generating PDF..."
        : `Download ${isQuotation ? "Quotation" : "Invoice"} PDF`
    }
  </PDFDownloadLink>
);

export default QuotPrintPDF;
